import React, {
  type ComponentType,
  type ReactNode,
  type SelectHTMLAttributes,
  useMemo,
} from "react";
import { Field } from "react-final-form";
import InputRendererNew from "src/shared/forms/formrenderers/InputRendererNew";
import type { IFormInputLabelRequired } from "src/shared/forms/inputs/IFormInput";
import { PleaseSelect } from "src/shared/forms/inputs/PleaseSelect";
import { combineValidations } from "src/shared/forms/validations/combineValidations";
import { required } from "src/shared/forms/validations/Required";
import type { ValidationHandler } from "src/shared/forms/validations/ValidationHandler";

export type SelectInputType<FormData = Record<string, unknown>> = ComponentType<
  Pick<
    ISelectInputProps<FormData>,
    | "inputID"
    | "label"
    | "isRequired"
    | "disabled"
    | "children"
    | "pleaseSelect"
  >
>;

export interface ISelectInputProps<FormData = Record<string, unknown>>
  extends IFormInputLabelRequired<FormData>,
    SelectHTMLAttributes<HTMLSelectElement> {
  isRequired?: boolean;
  children: ReactNode;
  pleaseSelect?: ReactNode;
  validators?: ValidationHandler | ValidationHandler[];
}

export const SelectInput = <FormData = Record<string, unknown>,>({
  isRequired,
  children,
  inputID,
  label,
  validationDisabled,
  pleaseSelect,
  validators,
  ...rest
}: ISelectInputProps<FormData>) => {
  const validations = useMemo(() => {
    if (validationDisabled) {
      return undefined;
    }
    return combineValidations(isRequired && required, validators);
  }, [validationDisabled, isRequired, validators]);

  return (
    <Field
      name={inputID}
      label={label}
      render={(props) => (
        <InputRendererNew label={label} {...rest} type="select" {...props} />
      )}
      validate={validations}
    >
      {pleaseSelect ?? <PleaseSelect />}
      {children}
    </Field>
  );
};
